import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEMANTIC, UI } from '../../styles/theme';
import KycProgressBar from '../KycProgressBar';
import API from '../../api';
import { tierToDisplayPlan } from '../../utils/membershipUtils';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

const MembershipSelection = () => {
  const [selected, setSelected] = useState('earth');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [plans, setPlans]       = useState([]);
  const [tiersLoading, setTiersLoading] = useState(true);
  const navigate = useNavigate();
  const userId   = localStorage.getItem('userId');
  const { t }    = useTranslation();
  const isMobile = useIsMobile();

  useEffect(() => {
    API.get('/membership-tiers')
      .then(res => setPlans((res.data || []).map(tier => tierToDisplayPlan(tier, t))))
      .catch(() => setError('Errore nel caricamento dei piani.'))
      .finally(() => setTiersLoading(false));
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleContinue = async () => {
    setSaving(true);
    setError('');
    try {
      await API.post('/kyc/membership', { user_id: userId, membership: selected });
      if (selected === 'earth') {
        navigate('/kyc/privacy-consents');
      } else {
        const selectedPlan = plans.find(p => p.key === selected);
        navigate('/kyc/payment-setup', { state: { plan: selectedPlan } });
      }
    } catch {
      setError(t('kyc.membership.error_save', 'Errore nel salvataggio. Riprova.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ backgroundColor: SEMANTIC.backgroundApp, minHeight: '100vh', display: 'flex', flexDirection: 'column', padding: isMobile ? UI.spacing.md : UI.spacing.lg }}>
      <div style={{ maxWidth: '960px', margin: '0 auto', width: '100%' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: UI.spacing.lg }}>
          <img src={`${process.env.PUBLIC_URL}/Logo_Kuesto.png`} alt="Kuesto" style={{ width: '100px' }} />
        </div>

        <KycProgressBar currentStep={5} totalSteps={10} />

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: isMobile ? UI.spacing.lg : UI.spacing.xl }}>
          <h2 style={{ color: SEMANTIC.actionBuy, fontSize: isMobile ? '20px' : UI.fontSize.title, fontWeight: UI.fontWeight.bold, marginBottom: UI.spacing.sm }}>
            {t('kyc.membership.title', 'Scegli il tuo piano')}
          </h2>
          <p style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.sm, margin: 0 }}>
            {t('kyc.membership.subtitle', 'Puoi cambiarlo in qualsiasi momento dalle impostazioni')}
          </p>
        </div>

        {tiersLoading ? (
          <div style={{ textAlign: 'center', color: SEMANTIC.textSecondary, padding: '40px 0' }}>
            Caricamento piani...
          </div>
        ) : (
          <>
            {/* ── Tab selector (mobile) ── */}
            {isMobile && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, justifyContent: 'center' }}>
                {plans.map(plan => (
                  <button
                    key={plan.key}
                    onClick={() => setSelected(plan.key)}
                    style={{
                      flex: 1, padding: '8px 4px',
                      borderRadius: UI.borderRadius.pill,
                      border: `2px solid ${selected === plan.key ? plan.color : 'rgba(255,255,255,0.12)'}`,
                      backgroundColor: selected === plan.key ? `${plan.color}22` : 'transparent',
                      color: selected === plan.key ? plan.color : SEMANTIC.textSecondary,
                      fontWeight: UI.fontWeight.bold, fontSize: 13, cursor: 'pointer',
                    }}
                  >
                    {plan.name}
                  </button>
                ))}
              </div>
            )}

            {/* ── Cards grid ── */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: isMobile ? 12 : UI.spacing.lg,
              marginBottom: UI.spacing.lg,
            }}>
              {plans
                .filter(plan => !isMobile || plan.key === selected)
                .map(plan => {
                  const isSelected = selected === plan.key;
                  return (
                    <div
                      key={plan.key}
                      onClick={() => setSelected(plan.key)}
                      style={{
                        position: 'relative',
                        backgroundColor: isSelected ? plan.bg : SEMANTIC.backgroundCard,
                        border: `2px solid ${isSelected ? plan.color : 'rgba(255,255,255,0.08)'}`,
                        borderRadius: UI.borderRadius.lg,
                        padding: isMobile ? '16px' : UI.spacing.xl,
                        cursor: 'pointer',
                        transition: UI.transition.fast,
                        boxShadow: isSelected ? `0 0 24px ${plan.color}30` : UI.shadow.card,
                      }}
                    >
                      {/* Popular badge */}
                      {plan.popular && (
                        <div style={{
                          position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                          backgroundColor: plan.color, color: '#000', fontSize: '11px', fontWeight: UI.fontWeight.bold,
                          padding: '3px 14px', borderRadius: UI.borderRadius.pill, whiteSpace: 'nowrap',
                        }}>
                          PIÙ SCELTO
                        </div>
                      )}

                      {isMobile ? (
                        /* ── MOBILE card layout ── */
                        <>
                          {/* Top row: icon + name/tagline + price */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                            {/* Planet */}
                            <div style={{ flexShrink: 0 }}>
                              {plan.img
                                ? <img src={plan.img} alt={plan.name} style={{ width: 64, height: 64, borderRadius: '50%' }} />
                                : <span style={{ fontSize: 52 }}>{plan.icon}</span>}
                            </div>
                            {/* Name + tagline */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ color: plan.color, fontSize: 20, fontWeight: UI.fontWeight.bold }}>{plan.name}</div>
                              <div style={{ color: SEMANTIC.textSecondary, fontSize: 12, marginTop: 2 }}>{plan.tagline}</div>
                            </div>
                            {/* Price */}
                            <div style={{
                              flexShrink: 0, textAlign: 'right',
                              backgroundColor: 'rgba(0,0,0,0.25)', borderRadius: UI.borderRadius.sm,
                              padding: '8px 12px',
                            }}>
                              <div style={{ color: plan.color, fontSize: 22, fontWeight: UI.fontWeight.bold, lineHeight: 1 }}>
                                {plan.pricing.value}
                              </div>
                              <div style={{ color: SEMANTIC.textSecondary, fontSize: 10, marginTop: 3 }}>
                                {plan.pricing.label}
                              </div>
                              {plan.pricing.sub && (
                                <div style={{ color: plan.color, fontSize: 11, marginTop: 2, opacity: 0.85 }}>
                                  {plan.pricing.sub}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Features: 2-column grid */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px' }}>
                            {plan.features.map(f => (
                              <div key={f.label} style={{
                                backgroundColor: 'rgba(0,0,0,0.15)', borderRadius: 8,
                                padding: '8px 10px',
                              }}>
                                <div style={{ color: SEMANTIC.textSecondary, fontSize: 10, marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.5 }}>{f.label}</div>
                                <div style={{
                                  fontSize: 13, fontWeight: UI.fontWeight.bold,
                                  color: f.highlight ? plan.color : f.muted ? '#555' : SEMANTIC.textPrimary,
                                }}>
                                  {f.value}
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        /* ── DESKTOP card layout (unchanged) ── */
                        <>
                          {/* Selected indicator */}
                          <div style={{
                            position: 'absolute', top: 14, right: 14, width: 20, height: 20,
                            borderRadius: '50%', border: `2px solid ${isSelected ? plan.color : 'rgba(255,255,255,0.2)'}`,
                            backgroundColor: isSelected ? plan.color : 'transparent',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {isSelected && <i className="bi bi-check" style={{ fontSize: 12, color: '#000', fontWeight: 'bold' }} />}
                          </div>

                          {/* Planet icon */}
                          <div style={{ textAlign: 'center', marginBottom: UI.spacing.md }}>
                            {plan.img
                              ? <img src={plan.img} alt={plan.name} style={{ width: 60, height: 60, borderRadius: '50%' }} />
                              : <span style={{ fontSize: 48 }}>{plan.icon}</span>}
                          </div>

                          {/* Name + tagline */}
                          <div style={{ textAlign: 'center', marginBottom: UI.spacing.lg }}>
                            <div style={{ color: plan.color, fontSize: UI.fontSize.xl, fontWeight: UI.fontWeight.bold }}>{plan.name}</div>
                            <div style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.sm, marginTop: 4 }}>{plan.tagline}</div>
                          </div>

                          {/* Pricing */}
                          <div style={{
                            textAlign: 'center', marginBottom: UI.spacing.lg, padding: UI.spacing.md,
                            backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: UI.borderRadius.sm,
                          }}>
                            <div style={{ color: plan.color, fontSize: '28px', fontWeight: UI.fontWeight.bold, lineHeight: 1 }}>
                              {plan.pricing.value}
                            </div>
                            <div style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.xs, marginTop: 4 }}>
                              {plan.pricing.label}
                            </div>
                            {plan.pricing.sub && (
                              <div style={{ color: plan.color, fontSize: UI.fontSize.sm, marginTop: 4, opacity: 0.85 }}>
                                {plan.pricing.sub}
                              </div>
                            )}
                          </div>

                          {/* Feature list */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {plan.features.map(f => (
                              <div key={f.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: 6 }}>
                                <span style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.sm }}>{f.label}</span>
                                <span style={{
                                  fontSize: UI.fontSize.sm, fontWeight: UI.fontWeight.bold,
                                  color: f.highlight ? plan.color : f.muted ? '#444' : SEMANTIC.textPrimary,
                                }}>
                                  {f.value}
                                </span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
            </div>

            {/* Error */}
            {error && (
              <div style={{ backgroundColor: 'rgba(255,77,77,0.1)', border: `2px solid ${SEMANTIC.actionSell}`, borderRadius: UI.borderRadius.sm, padding: UI.spacing.md, marginBottom: UI.spacing.lg, color: SEMANTIC.actionSell, textAlign: 'center' }}>
                {error}
              </div>
            )}

            {/* Note */}
            <div style={{ marginBottom: UI.spacing.lg, padding: UI.spacing.md, backgroundColor: 'rgba(0,255,136,0.05)', borderRadius: UI.borderRadius.sm, border: `1px solid ${SEMANTIC.actionBuy}` }}>
              <p style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.sm, margin: 0 }}>
                💡 {t('kyc.membership.note', 'La success fee viene applicata solo sui guadagni realizzati. Potrai cambiare piano in qualsiasi momento.')}
              </p>
            </div>

            {/* CTA */}
            <button
              onClick={handleContinue}
              disabled={saving}
              style={{
                width: '100%', padding: UI.spacing.md,
                backgroundColor: saving ? SEMANTIC.backgroundDisabled : SEMANTIC.ctaPrimary,
                color: saving ? SEMANTIC.textDisabled : SEMANTIC.ctaPrimaryText,
                border: 'none', borderRadius: UI.borderRadius.sm,
                fontSize: UI.fontSize.lg, fontWeight: UI.fontWeight.bold,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? '...' : t('common.continue', 'Continua')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default MembershipSelection;
